import {
  Component,
  OnInit,
  OnDestroy,
  ElementRef,
  ViewChild,
  HostListener,
  Output,
  EventEmitter,
  Input, HostBinding,
  ChangeDetectionStrategy
} from '@angular/core';
import { DataStorageService } from "../../services/data-storage.service";
import { AccountService } from "../../services/account.service";
import { Router } from "@angular/router";
import { map, Observable } from "rxjs";
import { GeneralConfirmationModalComponent } from "../general-confirmation-modal/general-confirmation-modal.component";
import { GeneralConfirmationModalData, GeneralConfirmationModalResult } from "../../models";
import { TranslateService } from "@ngx-translate/core";
import { DialogService } from "../../services/dialog.service";

declare global {
  interface Window {
    chatwootSDK: any;
    $chatwoot: any;
    chatwootSettings: any;
  }
}

@Component({
  selector: 'cpf-chat',
  templateUrl: './chat.component.html',
  styleUrls: ['./chat.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class ChatComponent implements OnInit, OnDestroy {
  @Input() @HostBinding('class.isDesktop') isDesktop = false;

  @ViewChild('chatContainer', { static: true }) chatContainer!: ElementRef;
  @Output() chatHistoryClicked = new EventEmitter<void>();
  private chatwootScript: HTMLScriptElement | null = null;
  private classListObserver: MutationObserver | null = null;
  private chatwootReadyListener: (() => void) | null = null;

  constructor(
    private dataStorageService: DataStorageService,
    private accountService: AccountService,
    private router: Router,
    private translate: TranslateService,
    private dialogService: DialogService
  ) { }

  ngOnInit() {
    this.loadChatwootScript();
  }

  navigateToChatHistory(): void {
    if (this.chatHistoryClicked.observed) {
      this.chatHistoryClicked.emit();
      return;
    }
    this.router.navigate(['calingo', 'telemedicine', 'chat-history']);
  }

  ngOnDestroy() {
    this.removeChatwootScript();
    this.cleanUpHtml()
    this.disconnectClassListObserver();
  }

  private loadChatwootScript() {
    const translations = this.translate.instant(['chat.launcher-title', 'chat.new-chat.title', 'chat.new-chat.description']);

    // Set Chatwoot settings for embedded mode
    window.chatwootSettings = {
      "hideMessageBubble": true,
      "position": "right",
      "type": "standard",
      "launcherTitle": translations['chat.launcher-title'],
      "welcomeTitle": translations['chat.new-chat.title'],
      "welcomeDescription": translations['chat.new-chat.description'],
      "showUnreadMessagesDialog": false,
      "showPopoutButton": false,
      "widgetStyle": "embedded",
      "darkMode": "light",
      "hideCloseButton": true,
      "locale": this.translate.getCurrentLang()
    };

    // Create and load the Chatwoot script
    this.chatwootScript = document.createElement('script');
    this.chatwootScript.src = 'https://chat.pivio.ai/packs/js/sdk.js';
    this.chatwootScript.defer = true;
    this.chatwootScript.async = true;

    // Register before appending the script so we never miss the event,
    // even if the browser delivers it synchronously on a cached load.
    this.chatwootReadyListener = () => {
      this.embedChatwoot();
    };
    window.addEventListener('chatwoot:ready', this.chatwootReadyListener, { once: true });

    this.chatwootScript.onload = () => {
      window.chatwootSDK.run({
        websiteToken: 'i2B5i16yceBbcnGUSUXrADF7',
        baseUrl: 'https://chat.pivio.ai'
      });
    };

    document.head.appendChild(this.chatwootScript);
  }

  private embedChatwoot() {
    if (window.$chatwoot) {
      // Move the chat widget to our container
      const widget = document.querySelector('#cw-widget-holder');
      if (widget && this.chatContainer) {
        this.chatContainer.nativeElement.innerHTML = '';
        this.chatContainer.nativeElement.appendChild(widget);

        this.observeWidgetClassList(widget);

        let iframe = widget.querySelector('iframe');

        iframe?.addEventListener('load', ()=>{
          if (this.accountService.user) {
            window.$chatwoot.setUser(this.accountService.user.email, {
              email: this.accountService.user.email,
              name: this.dataStorageService.selectedData?.firstname + ' ' + this.dataStorageService.selectedData?.lastname,
            });
          }
        })
      }

      // Open chat immediately
      window.$chatwoot.toggle('open');
    }
  }

  private removeChatwootScript() {
    if (this.chatwootReadyListener) {
      window.removeEventListener('chatwoot:ready', this.chatwootReadyListener);
      this.chatwootReadyListener = null;
    }

    if (window.$chatwoot) {
      window.$chatwoot.toggle('close');
    }

    if (this.chatwootScript) {
      document.head.removeChild(this.chatwootScript);
      this.chatwootScript = null;
    }

    // Clean up
    delete window.chatwootSettings;
    delete window.chatwootSDK;
    delete window.$chatwoot;

    const chatwootContainer = document.querySelector('#chatwoot_live_chat_widget');
    if (chatwootContainer) {
      chatwootContainer.remove();
    }
  }

  private observeWidgetClassList(widget: Element) {
    this.classListObserver = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'attributes' && mutation.attributeName === 'class') {
          const target = mutation.target as HTMLElement;
          const currentClasses = Array.from(target.classList);

          // Handle classList changes here
          this.onWidgetClassListChanged(currentClasses, target);
        }
      });
    });

    // Start observing
    this.classListObserver.observe(widget, {
      attributes: true,
      attributeFilter: ['class']
    });
  }

  private onWidgetClassListChanged(classes: string[], element: HTMLElement) {
    if (classes.includes('woot--hide')) {
      this.router.navigate(['calingo', 'pet']);
    }
  }

  private disconnectClassListObserver() {
    if (this.classListObserver) {
      this.classListObserver.disconnect();
      this.classListObserver = null;
    }
  }

  @HostListener('window:beforeunload')
  onBeforeUnload() {
    this.cleanUpHtml();
  }

  cleanUpHtml() {
    const widgetHolder = document.querySelector('#cw-widget-holder');
    if (widgetHolder) {
      widgetHolder.remove();
    }
  }

  canDeactivate(): boolean | Observable<boolean> {
    /*const translations = this.translate.instant(['chat.modals.leave-unsaved.title', 'chat.modals.leave-unsaved.message']);
    return this.dialogService.openDialog(GeneralConfirmationModalComponent, {
      data: {
        title: translations['chat.modals.leave-unsaved.title'],
        message: translations['chat.modals.leave-unsaved.message'],
        confirmButton: GeneralConfirmationModalResult.Confirm,
        cancelButton: GeneralConfirmationModalResult.Cancel,
      } as GeneralConfirmationModalData
    }).afterClosed().pipe(map((result: GeneralConfirmationModalResult) => {
      return result === GeneralConfirmationModalResult.Confirm;
    }));*/
    return true;
  }
}
